import { useState } from 'react';
import { useMutation } from 'react-relay';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { z } from 'zod';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { LoaderCircle, Mail } from 'lucide-react';
import { Button } from '@/components/ui/Button';
import { Input } from '@/components/ui/Input';
import { Label } from '@/components/ui/Label';
import { useAuth } from '@/contexts/AuthContext';
import { graphql } from 'react-relay';
import loginMutation from '@/__generated__/LoginPageMutation.graphql.ts';
import { AuthLayout } from '@/features/auth/components/AuthLayout';
import { PasswordField } from '@/features/auth/components/PasswordField';
import {
  captureEvent,
  createEventId,
  normalizeErrorCode,
} from '@/analytics/posthog';

const loginMutationGraphql = graphql`
  mutation LoginPageMutation($email: String!, $password: String!) {
    login(email: $email, password: $password) {
      token
      user {
        id
        name
        email
      }
    }
  }
`;

const loginSchema = z.object({
  email: z.string().email('Please enter a valid email'),
  password: z.string().min(1, 'Password is required'),
});

type LoginFormValues = z.infer<typeof loginSchema>;

export function LoginPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const nextPath = searchParams.get('next');
  const { login } = useAuth();
  const [commit, isInFlight] = useMutation(loginMutation);
  const [error, setError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginFormValues>({
    resolver: zodResolver(loginSchema),
  });

  const onSubmit = (values: LoginFormValues) => {
    setError(null);
    const eventId = createEventId('login');
    commit({
      variables: values,
      onCompleted: (response) => {
        if (response.login) {
          void login(response.login.token, response.login.user)
            .then(() => {
              captureEvent('login_success', {
                method: 'password',
                event_id: eventId,
              });
              // Honor a post-login redirect (used by /accept-invite and
              // similar deep links that bounce unauthenticated users here).
              navigate(nextPath && nextPath.startsWith('/') ? nextPath : '/dashboard');
            })
            .catch(() => {
              captureEvent('login_failed', {
                method: 'password',
                event_id: eventId,
                error_code: 'session_save_failed',
              });
              setError('Could not securely save your session. Please try again.');
            });
        }
      },
      onError: (err) => {
        captureEvent('login_failed', {
          method: 'password',
          event_id: eventId,
          error_code: normalizeErrorCode(err.message ?? ''),
        });
        const raw = err.message ?? '';
        const message = raw.includes('Invalid credentials')
          ? 'Invalid email or password'
          : 'Something went wrong. Please try again.';
        setError(message);
      },
    });
  };

  return (
    <AuthLayout title="Welcome back" subtitle="Sign in to continue to your workspace.">
      <form onSubmit={handleSubmit(onSubmit)} className="mt-8 space-y-5" noValidate>
        <div className="space-y-2">
          <Label htmlFor="email" className="text-sm font-medium text-foreground">
            Email
          </Label>
          <div className="relative">
            <Mail
              aria-hidden="true"
              className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            />
            <Input
              id="email"
              type="email"
              inputMode="email"
              autoComplete="email"
              placeholder="you@company.com"
              aria-invalid={errors.email ? 'true' : 'false'}
              aria-describedby={errors.email ? 'login-email-error' : undefined}
              className="h-12 rounded-xl border-input bg-background pl-10 pr-4 text-base sm:text-sm placeholder:text-muted-foreground focus-visible:ring-primary focus-visible:ring-offset-0"
              {...register('email')}
            />
          </div>
          {errors.email && (
            <p id="login-email-error" className="text-xs text-destructive">
              {errors.email.message}
            </p>
          )}
        </div>

        <PasswordField
          id="password"
          label="Password"
          autoComplete="current-password"
          placeholder="Enter your password"
          error={errors.password?.message}
          {...register('password')}
        />

        <div aria-live="polite">
          {error && (
            <p
              role="alert"
              className="rounded-xl border border-destructive/20 bg-destructive/10 px-4 py-3 text-sm font-medium text-destructive"
            >
              {error}
            </p>
          )}
        </div>

        <Button
          type="submit"
          size="lg"
          className="h-12 w-full rounded-xl text-base font-semibold shadow-lg shadow-primary/20"
          disabled={isInFlight}
        >
          {isInFlight && <LoaderCircle className="mr-2 h-4 w-4 animate-spin" aria-hidden="true" />}
          {isInFlight ? 'Signing in...' : 'Sign in'}
        </Button>
      </form>

      <p className="mt-8 text-center text-sm text-muted-foreground">
        New to Day Sheetly?{' '}
        <Link
          to="/register"
          className="font-semibold text-primary transition-colors hover:text-primary-hover"
        >
          Create an account
        </Link>
      </p>
    </AuthLayout>
  );
}
